import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { getDocumentId } from '../../features/document/selectors';
import FullscreenLoader from '../loader/FullscreenLoader';
import DocumentDetailsContent from './DocumentDetailsContent';
import {
  DocumentMatchParams,
  documentTypeToUrl,
  getUserFriendlyDocumentTemplateNamePlural,
  isPublicPage,
  PublicDocumentMatchParams,
  showDocumentDisplay,
  urlToDocumentType,
} from '../../scripts/utils';
import { fetchDocument, setDocument } from '../../features/document/actions';
import { getProjectState } from '../../features/project/selectors';
import { fetchProject } from '../../features/project/actions';
import { getDocumentsState, getDocumentsType } from '../../features/documents/selectors';
import { fetchDocumentsByType } from '../../features/documents/actions';
import {
  DocumentTemplateType,
  INumberedDocumentView,
  NotifiedUserType,
} from '../../api-client/autogenerated';
import DocumentDisplay from '../document-display/DocumentDisplay';
import {
  getDocumentLoadingState,
  getDocumentsLoadingState,
} from '../../features/loading/selectors';
import { getUserState } from '../../features/user/selectors';
import { getBidState } from '../../features/bid/selectors';
import { fetchBid, setBid } from '../../features/bid/actions';
import { Auth } from '@aws-amplify/auth';
import { useHistory } from 'react-router-dom';
import CreatePunchListPage from '../punch-lists/CreatePunchListPage';
import { Button, makeStyles, Typography, useMediaQuery } from '@material-ui/core';
import NavAppbar from '../nav-top/NavAppbar';

const useStyles = makeStyles((theme) => ({
  // Same padding as DocumentDetailsContent, so the top bar and breadcrumbs sit where they do on
  // any other document page.
  deletedPage: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    boxSizing: 'border-box',
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  deletedMessage: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  // The theme's heading variants are small and tinted, so the size is set here.
  deletedText: {
    fontSize: 28,
    lineHeight: '36px',
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  deletedButton: {
    marginTop: theme.spacing(3),
  },
}));

export default function DocumentDetailsPage(props: { indexDrawerOpen: boolean }) {
  const { indexDrawerOpen } = props;
  const classes = useStyles();
  const params = useParams<DocumentMatchParams>();
  const publicParams = useParams<PublicDocumentMatchParams>();
  const dispatch = useDispatch();
  const history = useHistory();
  const documentsType = useSelector(getDocumentsType);
  const documentId = useSelector(getDocumentId);
  const bid = useSelector(getBidState);
  const documentLoading = useSelector(getDocumentLoadingState);
  const documentsLoading = useSelector(getDocumentsLoadingState);
  const project = useSelector(getProjectState);
  const documents = useSelector(getDocumentsState);
  const user = useSelector(getUserState);
  const [visible, setVisible] = useState(false);
  // Set when fetchDocument genuinely fails (not found, no access) for the id currently in the
  // URL -- e.g. a stale or bad link. The retry the effect below now does on every loading-state
  // change means a merely *in-flight-at-the-wrong-moment* fetch (the actual cause of documents
  // never loading after creation) resolves itself; this only ever fires for a fetch that keeps
  // failing, so the page can offer a way out instead of spinning on FullscreenLoader forever.
  const [documentFetchFailedId, setDocumentFetchFailedId] = useState<string>();
  // Set alongside documentFetchFailedId when the read was refused because the document has been
  // deleted (the backend marks that 404 with isDocumentDeleted). Site admins can still open a
  // deleted document, so they never get here.
  const [deletedDocumentId, setDeletedDocumentId] = useState<string>();
  // The public route carries :key, not :documentId, so the guard the authenticated branch uses
  // -- "is the document I have the one the URL asks for?" -- cannot be expressed here. These
  // track the key this page has already gone out for, and the key a read genuinely failed on.
  const [publicFetchedKey, setPublicFetchedKey] = useState<string>();
  const [publicFetchFailedKey, setPublicFetchFailedKey] = useState<string>();
  // Which project-and-type this page has already asked the log for. `documents.length === 0`
  // cannot tell "not fetched yet" from "fetched, and this project has none of these", so on a
  // genuinely empty log it stayed true and the effect re-fetched on every loading transition.
  const [documentsFetchedFor, setDocumentsFetchedFor] = useState<string>();
  // The bid id whose read genuinely failed, so the Bid Tabulation branch stops asking for it.
  const [bidFetchFailedId, setBidFetchFailedId] = useState<string>();

  const isMobile = useMediaQuery('(max-width:600px)');

  const userType = new URLSearchParams(window.location.search).get('userType') as
    | NotifiedUserType
    | undefined;

  //Test code for quickly assigning a subcontractor
  /*
  useEffect(() => {
    editSubcontractorByDocumentId(
      '14087646-a9ae-44a5-b490-0b710f32cced',
      dayjs().toISOString(),
      'test@test.com',
    );
  }, []);
  */

  // getContent will return a DocumentDetailsContent component, passing it the relevant props per document type
  const getContent = () => {
    if (bid) {
      return <DocumentDetailsContent />;
    }
    switch (documentsType) {
      case DocumentTemplateType.CloseoutSubmittals:
      case DocumentTemplateType.Submittals:
      case DocumentTemplateType.PayApplications:
        return <DocumentDetailsContent enableCurrentlyResponsible enableAdditionalReview />;

      case DocumentTemplateType.PotentialChangeOrders:
      case DocumentTemplateType.RequestsForInformation:
      case DocumentTemplateType.RequestsForChange:
        return <DocumentDetailsContent enableCurrentlyResponsible enableAdditionalReview isRfi />;

      case DocumentTemplateType.WorkChangeProposalRequests:
        return <DocumentDetailsContent enableCurrentlyResponsible isWcpr />;

      case DocumentTemplateType.OperationsAndMaintenanceData:
      case DocumentTemplateType.AsBuilt:
        return <DocumentDetailsContent enableCurrentlyResponsible />;
      case DocumentTemplateType.Task:
      case DocumentTemplateType.CaTask:
      case DocumentTemplateType.DesignPackages:
        return <DocumentDetailsContent enableCurrentlyResponsible isRfi />;

      case DocumentTemplateType.PunchList:
        return <CreatePunchListPage mode="PUBLIC" />;

      // Bidder RFIs and Substitution Requests review like a Construction RFI, so they take the
      // same action center and the Confidential Consultant Review card. isRfi keeps the
      // submission step read-only (the bidder already filed it); the components drop the
      // contractor-response step for these two themselves.
      case DocumentTemplateType.BidderRfIs:
      case DocumentTemplateType.SubstitutionRequests:
        return <DocumentDetailsContent enableCurrentlyResponsible enableAdditionalReview isRfi />;

      // Base document
      case DocumentTemplateType.Specifications ||
        DocumentTemplateType.Addenda ||
        DocumentTemplateType.Drawings ||
        DocumentTemplateType.ChangeOrders ||
        DocumentTemplateType.InformationalItems ||
        DocumentTemplateType.PlanholderList ||
        DocumentTemplateType.RegulatoryApprovals ||
        DocumentTemplateType.CertificateOfSubstantialCompletion ||
        DocumentTemplateType.ClearLp ||
        DocumentTemplateType.PunchList ||
        DocumentTemplateType.WarrantyItems ||
        DocumentTemplateType.BidderRfIs ||
        DocumentTemplateType.SubstitutionRequests:
        return <DocumentDetailsContent />;
      // Base workflow document
      default:
        return <DocumentDetailsContent enableCurrentlyResponsible />;
    }
  };

  const isLoading = () => {
    if (!isPublicPage()) {
      if (bid) return false;
      return (
        documents.length === 0 ||
        !documentId ||
        !project ||
        documentId !== params.documentId ||
        documentLoading ||
        documentsType !== urlToDocumentType[params.type]
      );
    }
    return documentId === null;
  };

  useEffect(() => {
    if (!isPublicPage()) {
      if (
        (urlToDocumentType[params.type] as DocumentTemplateType) ===
        DocumentTemplateType.BidTabulation
      ) {
        dispatch(setDocument(null));
        // Read only while the bid this URL names is not the one loaded. This used to read it every
        // time the effect ran, and fetchBid raises and lowers the same loading flag this effect
        // watches, so each read's own settling ran the effect and read the bid again, for as long
        // as the page stayed open.
        //
        // Guarded on the bid actually loaded rather than on having asked once, because the
        // document branch below clears the bid and the same page instance serves both routes:
        // leaving for a document and coming back has to read it again. bidFetchFailedId is what
        // stops a bid that will never load from being asked for on every pass instead.
        if (
          (!bid || bid.id !== params.documentId) &&
          !documentLoading &&
          bidFetchFailedId !== params.documentId
        ) {
          const requestedBidId = params.documentId;
          dispatch(fetchBid(requestedBidId)).catch(() => setBidFetchFailedId(requestedBidId));
        }
        if (!project || project?.id !== params.projectId) dispatch(fetchProject(params.projectId));
      } else {
        dispatch(setBid(null));
        if (!project || project?.id !== params.projectId) dispatch(fetchProject(params.projectId));
        // documentFetchFailedId keeps the retry below from becoming a loop. The retry exists for
        // a fetch that was merely in flight for the wrong id and has now settled -- that one
        // succeeded, so documentId simply names the wrong document and the retry corrects it.
        // A fetch that genuinely failed leaves documentId unset, which keeps `!documentId` true
        // forever, so without this the effect re-fetched a document that will never load on
        // every loading transition, mailing an error report each time round.
        if (
          (!documentId || documentId !== params.documentId) &&
          !documentLoading &&
          documentFetchFailedId !== params.documentId
        ) {
          const requestedDocumentId = params.documentId;
          dispatch(fetchDocument(requestedDocumentId)).catch((error: any) => {
            if (error?.response?.data?.isDocumentDeleted) setDeletedDocumentId(requestedDocumentId);
            setDocumentFetchFailedId(requestedDocumentId);
          });
        }
        const documentsFetchKey = `${project?.id}|${params.type}`;
        if (
          project &&
          (documents.length === 0 || documentsType !== urlToDocumentType[params.type]) &&
          !documentsLoading &&
          documentsFetchedFor !== documentsFetchKey
        ) {
          // Claimed before dispatch, like the public read below: an empty result must not look
          // like a reason to ask again. Moving to another project or document type changes the
          // key, so those still fetch.
          setDocumentsFetchedFor(documentsFetchKey);
          dispatch(
            fetchDocumentsByType(
              project.id,
              urlToDocumentType[params.type] as DocumentTemplateType,
            ),
          );
        }
      }
    } else {
      // Claimed before the read goes out rather than after it lands. This used to ask whether
      // documentId matched params.documentId, which on this route is undefined -- there is no
      // :documentId in `/main/public/documents/:key` -- so the comparison was true forever, on a
      // successful read as much as on a failed one. That left documentLoading as the only brake,
      // and it clears at the end of every attempt, so the effect re-ran and read again: a tight
      // loop for as long as the page stayed open. Every failing pass sent an error email, and
      // every pass that got far enough to be served inserts a DocumentViewed notification of its
      // own -- a read refused for permission does not get that far.
      if (publicParams.key && publicFetchedKey !== publicParams.key && !documentLoading) {
        const requestedKey = publicParams.key;
        setPublicFetchedKey(requestedKey);
        dispatch(fetchDocument(requestedKey, true))
          .then((result: { payload?: INumberedDocumentView }) => {
            const documentTemplate = result.payload?.documentTemplate?.name as DocumentTemplateType;
            const shouldPreventRedirect =
              userType === NotifiedUserType.PlanHolder &&
              (documentTemplate === DocumentTemplateType.Addenda ||
                documentTemplate === DocumentTemplateType.InformationalItems);
            Auth.currentAuthenticatedUser()
              .then((data) => {
                if (
                  data.attributes.sub === sessionStorage.getItem('publicUserId') &&
                  result.payload?.documentTemplate?.name &&
                  !shouldPreventRedirect
                ) {
                  if (documentTemplate === DocumentTemplateType.PunchList) {
                    history.push(
                      `/main/projects/${result.payload.projectId}/documents/${documentTypeToUrl[documentTemplate]}/${result.payload.id}/edit`,
                      { publicRedirect: true },
                    );
                  } else {
                    history.push(
                      `/main/projects/${result.payload.projectId}/documents/${documentTypeToUrl[documentTemplate]}/${result.payload.id}`,
                      { publicRedirect: true },
                    );
                  }
                }
              })
              .catch(() => {});
          })
          // The read rejects on a bad or expired key, and on a document the key's user has no
          // permission for. It had no rejection handler at all, so the failure surfaced only as
          // an unhandled rejection and the page went on spinning through the loop above.
          .catch(() => setPublicFetchFailedKey(requestedKey));
      }
    }
    // documentLoading and documentsLoading are read in the guards above and belong in the
    // dependency list: when a fetch's loading flag clears, that transition is what has to
    // re-evaluate whether the *current* params.documentId still needs fetching. Missing them
    // here was why a fetch in flight for a stale document id (see handleDocumentCreated's
    // dispatch(setDocument(null)) rendering before its history.push takes effect, briefly
    // racing a fetch for the *old* id against the new URL) never got retried once that stale
    // fetch settled and left documentId still mismatched from the new params.documentId --
    // nothing else in this array changes afterward, so the effect never ran again and the page
    // spun on FullscreenLoader forever.
  }, [
    project,
    documentId,
    params,
    publicParams,
    publicFetchedKey,
    documentFetchFailedId,
    documentsFetchedFor,
    bid,
    bidFetchFailedId,
    documentsType,
    user,
    documentLoading,
    documentsLoading,
  ]);

  // A genuine failure (see documentFetchFailedId) rather than the ordinary loading state: the
  // one case the effect's own retry cannot resolve on its own.
  const documentFailedToLoad = isPublicPage()
    ? publicFetchFailedKey === publicParams.key && !documentId
    : documentFetchFailedId === params.documentId && documentId !== params.documentId;

  const documentIsDeleted = !isPublicPage() && deletedDocumentId === params.documentId;
  // The log for this document's type, taken from the URL. If the type cannot be resolved, fall
  // back to the project dashboard.
  const deletedDocumentType = urlToDocumentType[params.type] as DocumentTemplateType | undefined;

  return (
    <>
      {!isMobile && showDocumentDisplay(params.type) ? (
        <DocumentDisplay
          isDocumentDetailsPage
          indexDrawerOpen={indexDrawerOpen}
          visible={visible}
          setVisible={setVisible}
        />
      ) : null}
      <div style={{ overflowX: 'auto', width: '100%' }}>
        {documentIsDeleted ? (
          <main className={classes.deletedPage}>
            <NavAppbar />
            <div className={classes.deletedMessage}>
              <Typography className={classes.deletedText}>
                This document has been deleted
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                className={classes.deletedButton}
                onClick={() =>
                  history.push(
                    deletedDocumentType
                      ? `/main/projects/${params.projectId}/documents/${params.type}`
                      : `/main/projects/${params.projectId}`,
                  )
                }
              >
                {deletedDocumentType
                  ? `View ${getUserFriendlyDocumentTemplateNamePlural(deletedDocumentType)}`
                  : 'View project dashboard'}
              </Button>
            </div>
          </main>
        ) : documentFailedToLoad ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginTop: 48,
            }}
          >
            <Typography>This document could not be loaded.</Typography>
            {/* A public visitor has no log to go back to, and no projectId in the URL to build
                one from -- the link itself is all they were given. */}
            {!isPublicPage() ? (
              <Button
                variant="contained"
                color="primary"
                style={{ marginTop: 16 }}
                onClick={() =>
                  history.push(`/main/projects/${params.projectId}/documents/${params.type}`)
                }
              >
                Back to the log
              </Button>
            ) : null}
          </div>
        ) : isLoading() ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 48,
            }}
          >
            <FullscreenLoader />
          </div>
        ) : (
          getContent()
        )}
      </div>
    </>
  );
}
